import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Compass } from "lucide-react";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";

export function FrameworksIndex() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/frameworks").then((r) => setItems(r.data || [])); }, []);

  return (
    <PublicLayout>
      <section className="border-b border-[var(--ink)] bg-[var(--parchment)] py-16 sm:py-20" data-testid="frameworks-index">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="eyebrow">Proprietary Intellectual Property</div>
          <h1 className="font-display font-black text-[clamp(2.4rem,6vw,5rem)] mt-4 leading-[0.95] tracking-tight">
            Frameworks built<br/>
            <span className="italic font-medium text-[var(--oxblood)]">for the boardroom.</span>
          </h1>
          <p className="mt-6 font-display italic text-2xl text-[var(--ink-soft)] max-w-3xl">
            Seven proprietary frameworks that turn analysis into decisions — and decisions into outcomes.
          </p>
        </div>
      </section>

      <section className="border-b border-[var(--ink)] bg-[var(--parchment)] pb-16 sm:pb-20">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-px bg-[var(--ink)] border border-[var(--ink)]">
          {items.map((f) => (
            <Link key={f.slug} to={`/frameworks/${f.slug}`} className="bg-[var(--parchment)] p-7 sm:p-9 group hover:bg-white transition-colors" data-testid={`framework-list-${f.slug}`}>
              <div className="flex items-center justify-between">
                <Compass size={22} className="text-[var(--oxblood)]" />
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--ink-soft)]">{String(f.order).padStart(2, "0")} / 07</div>
              </div>
              <h3 className="font-display font-bold text-2xl sm:text-3xl mt-5 text-[var(--ink)] group-hover:text-[var(--oxblood)] transition-colors leading-tight">{f.name}</h3>
              <p className="mt-3 text-base text-[var(--oxblood)] font-display italic">{f.tagline}</p>
              <p className="mt-4 text-sm text-[var(--ink-soft)] leading-relaxed">{f.summary}</p>
              <div className="mt-6 text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--ink)] flex items-center gap-1">
                Explore framework <ArrowRight size={12} />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </PublicLayout>
  );
}

function Pipeline5({ stages = [] }) {
  return (
    <div className="grid sm:grid-cols-5 gap-px bg-[var(--ink)] border border-[var(--ink)]">
      {stages.map((s, i) => (
        <div key={i} className="bg-white p-5 sm:p-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--oxblood)]">Stage {i + 1}</div>
          <div className="font-display font-bold text-xl sm:text-2xl mt-2 text-[var(--ink)]">{s.label}</div>
          <p className="mt-3 text-sm text-[var(--ink-soft)] leading-relaxed">{s.detail}</p>
        </div>
      ))}
    </div>
  );
}

function Matrix2x2({ axes, quadrants = [] }) {
  return (
    <div className="bg-white border border-[var(--border-soft)] p-4 sm:p-8">
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--ink-soft)] mb-2">
        Y-axis ↑ {axes?.y} · X-axis → {axes?.x}
      </div>
      <div className="grid grid-cols-2 gap-2 aspect-square max-w-[560px] mx-auto">
        {[1, 0, 3, 2].map((idx) => {
          const q = quadrants[idx];
          if (!q) return <div key={idx} />;
          const isInvest = q.label === "Invest";
          return (
            <div key={idx} className={"p-5 border-2 " + (isInvest ? "border-[var(--oxblood)] bg-[var(--oxblood)]/5" : "border-[var(--border-soft)]")}>
              <div className="font-display font-black text-2xl text-[var(--ink)]">{q.label}</div>
              <p className="mt-2 text-xs text-[var(--ink-soft)] leading-relaxed">{q.detail}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Radar6({ pillars = [] }) {
  return (
    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {pillars.map((p, i) => (
        <li key={i} className="border border-[var(--border-soft)] bg-white p-4">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--oxblood)]">Pillar {String(i + 1).padStart(2, "0")}</div>
          <div className="font-display font-bold text-lg mt-1">{p}</div>
        </li>
      ))}
    </ul>
  );
}

function StackedBars({ dimensions = [] }) {
  return (
    <ul className="space-y-3">
      {dimensions.map((d, i) => (
        <li key={i} className="flex items-center gap-4">
          <div className="font-mono text-xs text-[var(--ink-soft)] w-8">{String(i + 1).padStart(2, "0")}</div>
          <div className="flex-1 font-display text-base">{d}</div>
          <div className="w-32 sm:w-48 h-2 bg-[var(--border-soft)] relative">
            <div className="absolute inset-y-0 left-0 bg-[var(--oxblood)]" style={{ width: `${65 + ((i * 7) % 30)}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function Phases3({ phases = [] }) {
  return (
    <div className="grid md:grid-cols-3 gap-4">
      {phases.map((p, i) => (
        <div key={i} className="border border-[var(--border-soft)] bg-white p-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--oxblood)]">Phase {i + 1} of 3</div>
          <div className="font-display font-bold text-xl mt-2 text-[var(--ink)]">{p.label}</div>
          <p className="mt-3 text-sm text-[var(--ink-soft)] leading-relaxed">{p.detail}</p>
        </div>
      ))}
    </div>
  );
}

function Curve5({ stages = [] }) {
  return (
    <div className="bg-white border border-[var(--border-soft)] p-6 sm:p-8">
      <div className="flex items-end justify-between gap-3 sm:gap-4 h-40">
        {stages.map((s, i) => {
          const heights = [25, 70, 95, 70, 35]; // bell curve
          return (
            <div key={i} className="flex-1 flex flex-col items-center">
              <div className="w-full bg-[var(--oxblood)]" style={{ height: `${heights[i] || 50}%` }} />
              <div className="mt-3 text-center text-[11px] font-mono uppercase tracking-wider text-[var(--ink-soft)]">{s}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Radial4({ branches = [] }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      {branches.map((b, i) => (
        <div key={i} className="border border-[var(--border-soft)] bg-white p-5">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--oxblood)]">Branch {String(i + 1).padStart(2, "0")}</div>
          <div className="font-display font-bold text-xl mt-2">{b}</div>
        </div>
      ))}
    </div>
  );
}

function Diagram({ f }) {
  switch (f.diagram_type) {
    case "pipeline_5": return <Pipeline5 stages={f.stages} />;
    case "matrix_2x2": return <Matrix2x2 axes={f.axes} quadrants={f.quadrants} />;
    case "radial_4": return <Radial4 branches={f.branches} />;
    case "radar_6": return <Radar6 pillars={f.pillars} />;
    case "stacked_bars": return <StackedBars dimensions={f.dimensions} />;
    case "phases_3": return <Phases3 phases={f.phases} />;
    case "curve_5": return <Curve5 stages={f.stages_curve} />;
    default: return null;
  }
}

export function FrameworkDetail() {
  const { slug } = useParams();
  const [f, setF] = useState(null);
  useEffect(() => { api.get(`/frameworks/${slug}`).then((r) => setF(r.data)).catch(() => {}); }, [slug]);
  if (!f) return <PublicLayout><div className="max-w-[1400px] mx-auto px-6 py-20 font-mono text-sm">Loading…</div></PublicLayout>;

  return (
    <PublicLayout>
      <section className="border-b border-[var(--ink)] bg-[var(--parchment)] py-16 sm:py-20" data-testid="framework-page">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
          <Link to="/frameworks" className="eyebrow hover:text-[var(--ink)]">← All frameworks</Link>
          <h1 className="font-display font-black text-[clamp(2.4rem,6vw,5rem)] mt-4 leading-[0.95] tracking-tight" data-testid="framework-title">{f.name}</h1>
          <p className="mt-6 font-display italic text-2xl text-[var(--ink-soft)]">{f.tagline}</p>
          <p className="mt-6 text-lg leading-relaxed max-w-3xl">{f.summary}</p>
        </div>
      </section>

      <section className="border-b border-[var(--ink)] bg-white py-16 sm:py-20">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
          <div className="eyebrow">The Framework</div>
          <div className="mt-6"><Diagram f={f} /></div>
        </div>
      </section>

      {f.when_to_use && (
        <section className="border-b border-[var(--ink)] bg-[var(--parchment)] py-16 sm:py-20">
          <div className="max-w-[1100px] mx-auto px-4 sm:px-6 grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-5">
              <div className="eyebrow">When to use</div>
            </div>
            <div className="md:col-span-7">
              <p className="font-display text-2xl sm:text-3xl leading-snug text-[var(--ink)]">{f.when_to_use}</p>
            </div>
          </div>
        </section>
      )}

      <section className="border-b border-[var(--ink)] bg-[var(--ink)] text-[var(--parchment)] py-16 sm:py-20">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-display font-black text-[clamp(1.8rem,4vw,3rem)] leading-tight">
            Apply <span className="italic font-medium text-[var(--brass)]">{f.name}</span> to your business.
          </h2>
          <p className="mt-5 text-lg text-[var(--parchment)]/80">A strategy session translates the framework into action.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/strategy-session" className="btn-primary" data-testid="framework-cta-session">
              Schedule a Strategy Session <ArrowRight size={14} />
            </Link>
            <Link to="/luminous" className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--brass)] hover:text-white inline-flex items-center gap-2">
              Try Luminous Intelligence →
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
